import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { inject } from '@angular/core';
import { ErrorStore } from '@application/errors/data-access';
import { HttpErrorResponse } from '@angular/common/http';
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from '@application/data-access';
import { pipe, switchMap, tap } from 'rxjs';
import { WhatsappService } from '../infra/whatsapp.service';
import { TWhatsappAccount, TWhatsappStatus } from '../entities/whatsapp.model';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { ICountryCodesPhoneNumber, TelInputStore } from "@shared/components/ui/tel-input";
import { tapResponse } from '@ngrx/operators';

export interface IWhatsappStore {
    showNewWhatsappModal: boolean;
    showRevalidateWhatsappModal?: boolean;
    whatsappAccount: TWhatsappAccount;
    savedWhatsappAccount?: TWhatsappAccount;
    status?: TWhatsappStatus;
    qrCode?: string;
    connectionState?: string;
}

export const whatsappInitialStore: IWhatsappStore = {
    showNewWhatsappModal: false,
    showRevalidateWhatsappModal: false,
    whatsappAccount: null,
    savedWhatsappAccount: null,
    status: null,
    qrCode: null,
    connectionState: null
};

export const WhatsappStore = signalStore(
  { providedIn: 'root' },
  withState<IWhatsappStore>(whatsappInitialStore),
  withMethods((store, errorStore = inject(ErrorStore), whatsappService = inject(WhatsappService), telInputStore = inject(TelInputStore)) => {

    const formatPhone = (phone): any => {
      const phoneNumber = parsePhoneNumberFromString('+' + phone);
      const country = phoneNumber?.country;
      if (country) {
        const countryObj = telInputStore.phoneNumberCountryCode()?.find(
          (item: ICountryCodesPhoneNumber) => item?.Region === country
        );
  
        let number: string = phoneNumber?.nationalNumber;
        if (phoneNumber?.nationalNumber.length === 10) {
          number = phoneNumber?.nationalNumber?.replace(/^(\+?62)(\d)/, '$19$2');
        }

        return { nationalNumber: number, mask: countryObj};
      }
      return '';
    }

    const generateQrCodeImage = (qrCodeImageData: string) => {
      if (qrCodeImageData.startsWith('data:image/')) {
        patchState(store, { qrCode: qrCodeImageData });
        return
      } 
      patchState(store, { qrCode: `data:image/png;base64,${qrCodeImageData}` })
    }

    return {
        formatPhone, 
        generateQrCodeImage,

        toggleNewWhatsappModal: (showNewWhatsappModal = !store.showNewWhatsappModal()) => {
            patchState(store, { showNewWhatsappModal });
        },

        toggleRevalidateWhatsappModal: (showRevalidateWhatsappModal = !store.showRevalidateWhatsappModal()) => {
            patchState(store, { showRevalidateWhatsappModal });
        },

        setStatusInitialState: () => {
          patchState(store, { ...setPropInit('getStatus')})
        },

        setWhatsappAccountInitialState: () => {
          patchState(store, { ...setPropInit('getWhatsappAccount')})
        },

        setPropsInitialState: () => {
          patchState(store, { qrCode: null, savedWhatsappAccount: null, status: null, ...setPropInit('startConnection', 'saveCellphoneNumber', 'getStatus')})
        },

        setConnectionState: (connectionState: string) => {
          patchState(store, { connectionState });
        },

        blankState: () => {
          patchState(store, whatsappInitialStore, setPropInit('getWhatsappAccount', 'startConnection', 'getStatus', 'removePhone', 'saveCellphoneNumber', 'disconnect'));
        },

        disconnect: rxMethod<string>(pipe(
          tap(() => patchState(store, { ...setPropLoading('disconnect') })),
          switchMap((payload) => whatsappService.disconnect(payload).pipe(
            tapResponse({
              next: () => {
                  patchState(store, {...setPropLoaded('disconnect')})
              },
              error: (error: HttpErrorResponse) => {
                console.error(error);
                patchState( store, { ...setPropError('disconnect', error) });
                errorStore.setHttpError(error)
              }
            }),
          ))
        )), 

        startConnection: rxMethod<string>(pipe(
            tap(() => patchState(store, { ...setPropLoading('startConnection') })),
            switchMap((payload) => whatsappService.startConnection(payload).pipe(
              tapResponse({
                next: () => {
                    patchState(store, { ...setPropLoaded('startConnection')})
                },
                error: (error: HttpErrorResponse) => {
                  console.error(error);
                  patchState( store, { ...setPropError('startConnection', error) });
                  errorStore.setHttpError(error)
                }
              }),
            ))
        )), 

        saveCellphoneNumber: rxMethod<string>(pipe(
            tap(() => patchState(store, { ...setPropLoading('saveCellphoneNumber') })),
            switchMap((payload) => whatsappService.saveCellphoneNumber(payload).pipe(
              tapResponse({
                next: (savedWhatsappAccount) => {
                    patchState(store, { savedWhatsappAccount, ...setPropLoaded('saveCellphoneNumber')})
                },
                error: (error: HttpErrorResponse) => {
                  console.error(error);
                  patchState( store, { ...setPropError('saveCellphoneNumber', error) });
                  errorStore.setHttpError(error)
                }
              }),
            ))
        )), 

        getStatus: rxMethod<string>(pipe(
            tap(() => patchState(store, { ...setPropLoading('getStatus') })),
            switchMap((payload) => whatsappService.getStatus(payload).pipe(
              tapResponse({
                next: (status) => {
                    patchState(store, { status, ...setPropLoaded('getStatus')})
                },
                error: (error: HttpErrorResponse) => {
                  console.error(error);
                  patchState(store, {status: null ,...setPropError('getStatus', error)});
                }
              }),
            ))
        )), 

        removePhone: rxMethod<string>(pipe(
            tap(() => patchState(store, { ...setPropLoading('removePhone') })),
            switchMap((payload) => whatsappService.remove(payload).pipe(
              tapResponse({
                next: (status) => {
                    patchState(store, { status, ...setPropLoaded('removePhone')})
                },
                error: (error: HttpErrorResponse) => {
                  patchState( store, { ...setPropError('removePhone', error) });
                }
              }),
            ))
        )), 
        
        getWhatsappAccount: rxMethod<void>(pipe(
            tap(() => patchState(store, { ...setPropLoading('getWhatsappAccount') })),
            switchMap(() => whatsappService.getWhatsappAccount().pipe(
              tapResponse({
                next: (whatsappAccount) => {
                    patchState(store, { whatsappAccount, ...setPropLoaded('getWhatsappAccount')})
                },
                error: (error: HttpErrorResponse) => {
                  console.error(error);
                  patchState( store, { ...setPropError('getWhatsappAccount', error) });
                  errorStore.setHttpError(error)
                }
              }),
            ))
        )),
    }
  }),
  withReqState('getWhatsappAccount', 'startConnection', 'getStatus', 'removePhone', 'saveCellphoneNumber', 'disconnect')
);


